import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import LightboxModal from '../components/LightboxModal';
import { staffMembers } from '../data/staffData';
import { schoolInfo, navigationLinks } from '../data/navigationData';
import { scrapedPages } from '../data/scrapedPagesData';

export default function PageDetail() {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    // Look in scrapedPages first for canonical, complete text
    const localMatch = scrapedPages[slug] || (slug === 'om-skolen' ? scrapedPages['om-os'] : null);

    // Fetch CMS pages collection with relative path
    fetch('/_api/public/content/sider')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente side fra CMS');
        return res.json();
      })
      .then((items) => {
        if (Array.isArray(items)) {
          const cmsMatch = items.find(
            (p) => p.url_slug === slug || (p.slug && p.slug._ === slug)
          );
          if (cmsMatch) {
            const cmsContent = cmsMatch.indhold || cmsMatch.content;
            const cmsTitle = cmsMatch.titel || cmsMatch.title;
            const cmsSummary = cmsMatch.resume || cmsMatch.summary;
            const cmsHero = (cmsMatch.hero_billede && cmsMatch.hero_billede.length > 0)
              ? cmsMatch.hero_billede
              : ((cmsMatch.hero_image && cmsMatch.hero_image.length > 0) ? cmsMatch.hero_image : null);

            // Merge with localMatch so full rich text is always preserved if CMS has brief text
            setPageData({
              ...cmsMatch,
              title: cmsTitle || localMatch?.title,
              summary: cmsSummary || localMatch?.summary,
              content: cmsContent || localMatch?.content,
              hero_image: cmsHero || localMatch?.hero_image
            });

            // If page has a connected gallery, fetch it
            if (cmsMatch.gallery_slug) {
              fetch('/_api/public/content/gallerier')
                .then((gRes) => (gRes.ok ? gRes.json() : []))
                .then((galleries) => {
                  if (Array.isArray(galleries)) {
                    const gal = galleries.find(
                      (g) => g.url_slug === cmsMatch.gallery_slug || (g.slug && g.slug._ === cmsMatch.gallery_slug)
                    );
                    const images = gal?.billeder || gal?.images;
                    if (images) {
                      setGalleryImages(images);
                    }
                  }
                })
                .catch(() => {});
            } else if (localMatch?.gallery_images?.length > 0) {
              setGalleryImages(localMatch.gallery_images);
            }
          } else if (localMatch) {
            setPageData(localMatch);
            if (localMatch.gallery_images?.length > 0) {
              setGalleryImages(localMatch.gallery_images);
            }
          } else {
            setError('Siden blev ikke fundet.');
          }
        } else if (localMatch) {
          setPageData(localMatch);
          if (localMatch.gallery_images?.length > 0) {
            setGalleryImages(localMatch.gallery_images);
          }
        } else {
          setError('Siden blev ikke fundet.');
        }
      })
      .catch(() => {
        // Fallback to complete scraped pages on any network or CMS failure
        if (localMatch) {
          setPageData(localMatch);
          if (localMatch.gallery_images?.length > 0) {
            setGalleryImages(localMatch.gallery_images);
          }
        } else {
          setError('Der opstod en fejl under indlæsning af siden.');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#f8faf5]">
        <div className="text-center space-y-3">
          <i className="fa-solid fa-spinner fa-spin text-3xl text-[#264A35]" aria-hidden="true" />
          <p className="text-sm font-medium text-[#5A6961]">Indlæser indhold...</p>
        </div>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#f8faf5] px-4 py-16">
        <div className="max-w-md text-center bg-[#eef4e6] p-8 rounded-3xl border border-[#d9e5d4] space-y-4">
          <i className="fa-solid fa-circle-question text-4xl text-[#C85A32]" aria-hidden="true" />
          <h1 className="text-2xl font-bold text-[#1D2923] font-serif-heading">
            Siden blev ikke fundet
          </h1>
          <p className="text-sm text-[#5A6961]">
            Vi kunne desværre ikke finde den side, du ledte efter.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-white bg-[#264A35] hover:bg-[#1A3626]"
          >
            <i className="fa-solid fa-house text-xs" aria-hidden="true" />
            <span>Gå til forsiden</span>
          </Link>
        </div>
      </div>
    );
  }

  const isStaffPage = slug === 'medarbejdere';

  // Find relevant sibling links in the same category for the sidebar
  const currentCategory = navigationLinks.find((cat) =>
    cat.children?.some((child) => child.href === `/side/${slug}`)
  );
  const siblingLinks = currentCategory?.children?.filter((c) => c.href !== `/side/${slug}`) || [];

  return (
    <div className="bg-white pb-20">
      
      {/* Subpage Header Banner following container line */}
      <section className="bg-[#eef4e6] border-b border-[#d9e5d4] py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl space-y-4">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1D2923] tracking-tight font-serif-heading">
              {pageData.title}
            </h1>

            {/* Summary */}
            {pageData.summary && (
              <p className="text-base sm:text-lg text-[#5A6961] leading-relaxed pt-1">
                {pageData.summary}
              </p>
            )}
          </div>

        </div>
      </section>

      {/* Main Content Area following container line */}
      <section className="py-12 lg:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {isStaffPage ? (
          /* Full width Staff Grid */
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {staffMembers.map((member) => (
                <div
                  key={member.name}
                  className="bg-white rounded-2xl border border-[#d9e5d4] overflow-hidden shadow-xs hover:shadow-md transition-all flex flex-col justify-between"
                >
                  <div>
                    <div className="h-60 overflow-hidden bg-[#eef4e6]">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover object-top transition-transform duration-300 hover:scale-105"
                      />
                    </div>
                    <div className="p-5">
                      <h2 className="text-base font-bold text-[#1D2923] font-serif-heading">
                        {member.name}
                      </h2>
                      <div className="text-xs font-semibold text-[#C85A32] uppercase tracking-wide mt-0.5">
                        {member.role}
                      </div>
                      {member.subjects && (
                        <p className="text-xs text-[#5A6961] mt-2 leading-relaxed">
                          {member.subjects}
                        </p>
                      )}
                    </div>
                  </div>

                  {(member.email || member.phone) && (
                    <div className="px-5 pb-5 pt-2 border-t border-[#d9e5d4] text-xs text-[#5A6961] space-y-1">
                      {member.email && (
                        <div className="truncate">
                          <a href={`mailto:${member.email}`} className="hover:text-[#264A35] font-semibold whitespace-nowrap">
                            {member.email}
                          </a>
                        </div>
                      )}
                      {member.phone && (
                        <div>
                          <a href={`tel:${member.phone.replace(/\s/g, '')}`} className="hover:text-[#264A35] font-semibold whitespace-nowrap">
                            {member.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* 12-column layout aligning to container */
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            
            {/* Main Content Column */}
            <div className="lg:col-span-8 space-y-8">
              
              {/* Optional Hero Image */}
              {pageData.hero_image && pageData.hero_image.length > 0 && (
                <div className="rounded-3xl overflow-hidden shadow-xs border border-[#d9e5d4] bg-[#eef4e6]">
                  <img
                    src={pageData.hero_image[0]}
                    alt={pageData.title}
                    className="w-full h-auto max-h-[460px] object-cover"
                  />
                </div>
              )}

              {/* Styled Rich Text Content */}
              <div
                className="text-[#3E4E45] text-base sm:text-lg leading-relaxed
                  [&>p]:mb-6 [&>p]:leading-relaxed [&>p]:text-[#3E4E45]
                  [&>h2]:text-2xl [&>h2]:sm:text-3xl [&>h2]:font-bold [&>h2]:text-[#1D2923] [&>h2]:font-serif-heading [&>h2]:mt-10 [&>h2]:mb-4 [&>h2]:tracking-tight
                  [&>h3]:text-xl [&>h3]:sm:text-2xl [&>h3]:font-bold [&>h3]:text-[#1D2923] [&>h3]:font-serif-heading [&>h3]:mt-8 [&>h3]:mb-3
                  [&>h4]:text-lg [&>h4]:font-bold [&>h4]:text-[#1D2923] [&>h4]:font-serif-heading [&>h4]:mt-6 [&>h4]:mb-2
                  [&>ul]:space-y-2.5 [&>ul]:my-6 [&>ul]:pl-6 [&>ul]:list-disc [&>ul]:marker:text-[#264A35]
                  [&>ol]:space-y-2.5 [&>ol]:my-6 [&>ol]:pl-6 [&>ol]:list-decimal [&>ol]:marker:text-[#264A35]
                  [&>li]:leading-relaxed [&>li]:text-[#3E4E45]
                  [&>blockquote]:border-l-4 [&>blockquote]:border-[#264A35] [&>blockquote]:bg-[#eef4e6] [&>blockquote]:p-5 [&>blockquote]:rounded-r-2xl [&>blockquote]:my-6 [&>blockquote]:text-[#1D2923] [&>blockquote]:italic
                  [&>strong]:text-[#1D2923] [&>strong]:font-bold
                  [&>a]:text-[#264A35] [&>a]:font-bold [&>a]:underline hover:[&>a]:text-black [&>a]:transition-colors
                  [&>table]:w-full [&>table]:border-collapse [&>table]:my-6 [&>table]:rounded-xl [&>table]:overflow-hidden [&>table_th]:bg-[#eef4e6] [&>table_th]:p-3 [&>table_td]:p-3 [&>table_td]:border-b [&>table_td]:border-[#d9e5d4] [&>hr]:my-8 [&>hr]:border-[#d9e5d4]
                  [&_img]:rounded-2xl [&_img]:my-6 [&_img]:shadow-xs [&_img]:border [&_img]:border-[#d9e5d4] [&_img]:max-w-full [&_img]:h-auto
                  [&_figure]:my-8 [&_figure]:rounded-2xl [&_figure]:overflow-hidden"
                dangerouslySetInnerHTML={{ __html: pageData.content }}
              />

              {/* Connected Gallery Section */}
              {galleryImages.length > 0 && !pageData.content?.includes(galleryImages[0]) && (
                <div className="pt-10 border-t border-[#d9e5d4]">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-[#1D2923] font-serif-heading">
                      {slug === 'udlandsrejse' ? 'Billeder fra udlandsrejsen' : (pageData.gallery_title || 'Billeder fra hverdagen')}
                    </h3>
                    <span className="text-xs font-semibold text-[#5A6961] bg-[#eef4e6] px-3 py-1 rounded-full border border-[#d9e5d4]">
                      {galleryImages.length} fotos
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {galleryImages.map((imgUrl, idx) => (
                      <button
                        key={imgUrl}
                        type="button"
                        onClick={() => setLightboxIndex(idx)}
                        className="group relative rounded-2xl overflow-hidden aspect-4/3 bg-[#eef4e6] shadow-xs hover:shadow-md transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#264A35]"
                      >
                        <img
                          src={imgUrl}
                          alt={`${pageData.title} billede ${idx + 1}`}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <i className="fa-solid fa-expand text-white text-lg drop-shadow" aria-hidden="true" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Bottom CTA Box */}
              <div className="bg-[#eef4e6] p-8 rounded-3xl border border-[#d9e5d4] flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left mt-10">
                <div className="space-y-1 max-w-md">
                  <h3 className="text-lg sm:text-xl font-bold text-[#1D2923] font-serif-heading">
                    Vil du vide mere eller opleve skolen?
                  </h3>
                  <p className="text-xs sm:text-sm text-[#5A6961]">
                    Vi står altid klar til en uforpligtende snak og en rundvisning på skolen.
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Link
                    to="/besoeg-skolen"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#264A35] bg-white hover:bg-[#f8faf5] border border-[#d9e5d4] transition-colors"
                  >
                    <span>Besøg skolen</span>
                  </Link>
                  <Link
                    to="/skriv-dit-barn-op"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-white bg-[#264A35] hover:bg-[#1D2923] shadow-xs transition-all"
                  >
                    <i className="fa-solid fa-pen-nib text-xs" aria-hidden="true" />
                    <span>Skriv dit barn op</span>
                  </Link>
                </div>
              </div>

            </div>

            {/* Sidebar Column */}
            <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-28">
              
              {/* Sibling navigation in category */}
              {siblingLinks.length > 0 && (
                <div className="bg-white rounded-3xl p-6 border border-[#d9e5d4] shadow-xs space-y-4">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-[#1D2923]">
                    {currentCategory?.title || 'Relaterede sider'}
                  </h3>
                  <ul className="space-y-2">
                    {siblingLinks.map((link) => (
                      <li key={link.href}>
                        <Link
                          to={link.href}
                          className="group flex items-center justify-between p-2.5 rounded-xl hover:bg-[#eef4e6] text-sm text-[#3E4E45] hover:text-[#1D2923] font-medium transition-all"
                        >
                          <span>{link.title}</span>
                          <i className="fa-solid fa-chevron-right text-xs text-[#88A68E] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Quick Contact & Action Card */}
              <div className="bg-white rounded-3xl p-6 border border-[#d9e5d4] shadow-xs space-y-5">
                <h3 className="text-base font-bold text-[#1D2923] font-serif-heading">
                  Odder Lille Friskole
                </h3>
                
                <div className="space-y-3 text-xs sm:text-sm text-[#3E4E45]">
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-location-dot text-[#264A35] w-4" aria-hidden="true" />
                    <span>{schoolInfo.address}, {schoolInfo.city}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-phone text-[#264A35] w-4" aria-hidden="true" />
                    <a href={`tel:${schoolInfo.phoneRaw}`} className="font-bold text-[#264A35] hover:underline">
                      {schoolInfo.phone}
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-envelope text-[#264A35] w-4" aria-hidden="true" />
                    <a href={`mailto:${schoolInfo.email}`} className="font-bold text-[#264A35] hover:underline truncate">
                      {schoolInfo.email}
                    </a>
                  </div>
                </div>

                <div className="pt-3 border-t border-[#d9e5d4] flex flex-col gap-2">
                  <Link
                    to="/besoeg-skolen"
                    className="w-full text-center px-4 py-2.5 rounded-full text-xs font-bold text-white bg-[#264A35] hover:bg-[#1D2923] transition-all shadow-xs"
                  >
                    Book en rundvisning
                  </Link>
                  <Link
                    to="/skriv-dit-barn-op"
                    className="w-full text-center px-4 py-2.5 rounded-full text-xs font-semibold text-[#264A35] bg-white hover:bg-[#f8faf5] border border-[#d9e5d4] transition-all"
                  >
                    Opskrivning
                  </Link>
                </div>
              </div>

            </aside>

          </div>
        )}

      </section>

      {/* Lightbox for Gallery */}
      <LightboxModal
        images={galleryImages}
        activeIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNext={() => setLightboxIndex((prev) => (prev + 1) % galleryImages.length)}
        onPrev={() => setLightboxIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
      />

    </div>
  );
}
